import { Card } from "@/components/ui/card";
import { LucideIcon, TrendingUp, TrendingDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface MetricCardProps {
  title: string;
  value: string;
  icon: LucideIcon;
  trend?: {
    value: string;
    positive: boolean;
  };
  variant?: "default" | "accent" | "warning" | "destructive";
  className?: string;
}

export function MetricCard({ title, value, icon: Icon, trend, variant = "default", className }: MetricCardProps) {
  const variantConfig = {
    default: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(0,114,181,0.4)]",
      iconBg: "from-[#0072B5] via-[#0090B5] to-[#00B5B0]",
      accentLine: "from-[#0072B5] via-[#0090B5] to-[#00B5B0]",
      textGradient: "from-[#0072B5] to-[#00B5B0]",
      glowColor: "rgba(0,114,181,0.15)",
    },
    accent: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(16,185,129,0.4)]",
      iconBg: "from-emerald-500 via-emerald-600 to-teal-600",
      accentLine: "from-emerald-500 via-teal-500 to-cyan-500",
      textGradient: "from-emerald-500 to-teal-600",
      glowColor: "rgba(16,185,129,0.15)",
    },
    warning: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(245,158,11,0.4)]",
      iconBg: "from-amber-500 via-orange-500 to-orange-600",
      accentLine: "from-amber-500 via-orange-500 to-red-500",
      textGradient: "from-amber-500 to-orange-600",
      glowColor: "rgba(245,158,11,0.15)",
    },
    destructive: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(239,68,68,0.4)]",
      iconBg: "from-red-500 via-rose-500 to-pink-600",
      accentLine: "from-red-500 via-rose-500 to-pink-500",
      textGradient: "from-red-500 to-pink-600",
      glowColor: "rgba(239,68,68,0.15)",
    },
  };

  const config = variantConfig[variant];

  return (
    <Card className={cn(
      "group relative overflow-hidden rounded-2xl",
      "bg-gradient-to-br from-background/95 via-background/90 to-muted/50",
      "backdrop-blur-xl",
      "border border-white/10 dark:border-white/5",
      "hover:border-white/20 dark:hover:border-white/10",
      "transition-all duration-500 ease-out",
      "hover:-translate-y-1",
      config.glow,
      className
    )}>
      {/* Animated gradient border */}
      <div className={cn(
        "absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500",
        "bg-gradient-to-r p-[1px]",
        config.accentLine
      )}>
        <div className="absolute inset-[1px] rounded-2xl bg-background/95" />
      </div>
      
      {/* Background glow orbs */}
      <div 
        className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-all duration-700"
        style={{ background: `radial-gradient(circle, ${config.glowColor}, transparent 70%)` }}
      />
      <div 
        className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-2xl opacity-0 group-hover:opacity-40 transition-all duration-700 delay-100"
        style={{ background: `radial-gradient(circle, ${config.glowColor}, transparent 70%)` }}
      />
      
      {/* Mesh pattern overlay */}
      <div className="absolute inset-0 opacity-[0.02] group-hover:opacity-[0.04] transition-opacity duration-500" 
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
          backgroundSize: '24px 24px'
        }}
      />
      
      <div className="relative p-5">
        <div className="flex items-start justify-between gap-4">
          {/* Icon with enhanced styling */}
          <div className="relative">
            {/* Icon glow */}
            <div 
              className={cn(
                "absolute inset-0 rounded-2xl blur-xl opacity-40 group-hover:opacity-70 transition-opacity duration-500",
                `bg-gradient-to-br ${config.iconBg}`
              )}
            />
            <div className={cn(
              "relative p-3.5 rounded-2xl",
              `bg-gradient-to-br ${config.iconBg}`,
              "shadow-lg",
              "ring-1 ring-white/25",
              "group-hover:scale-110 group-hover:rotate-[-6deg] transition-all duration-500 ease-out"
            )}>
              <Icon className="h-5 w-5 text-white drop-shadow-md" strokeWidth={2.5} />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 text-right">
            <p className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider mb-1">
              {title}
            </p>
            <div className={cn(
              "text-2xl font-bold tracking-tight",
              "bg-gradient-to-r bg-clip-text text-transparent",
              config.textGradient
            )}>
              {value}
            </div>
            {trend && (
              <div className={cn(
                "flex items-center justify-end gap-1.5 mt-2 text-xs font-medium",
                trend.positive ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400"
              )}>
                <span className={cn(
                  "flex items-center justify-center w-5 h-5 rounded-full",
                  trend.positive 
                    ? "bg-emerald-100 dark:bg-emerald-500/20" 
                    : "bg-red-100 dark:bg-red-500/20"
                )}>
                  {trend.positive 
                    ? <TrendingUp className="w-3 h-3" /> 
                    : <TrendingDown className="w-3 h-3" />
                  }
                </span>
                <span>{trend.value}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Bottom decorative element */}
        <div className="mt-4 flex items-center gap-2">
          <div className={cn(
            "flex-1 h-1 rounded-full bg-gradient-to-r opacity-20 group-hover:opacity-40 transition-opacity duration-500",
            config.accentLine
          )} />
          <div className={cn(
            "w-1.5 h-1.5 rounded-full bg-gradient-to-r",
            config.accentLine,
            "animate-pulse"
          )} />
        </div>
      </div>
    </Card>
  );
}
